if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();
      
      // Client timezone detection
      this.useClientTimezone = this.dataset.useClientTz === 'true';
      this.resetIntervalDays = parseInt(this.dataset.resetInterval) || 0;
      this.hideOnComplete = this.dataset.hideOnComplete === 'true';
      
      // Calculate end date
      if (this.useClientTimezone && this.resetIntervalDays > 0) {
        // Client timezone mode with auto-reset
        this.endDate = this.calculateClientTimezoneReset();
      } else {
        // Standard mode - use provided end date
        this.endDate = new Date(this.dataset.endDate).getTime();
      }
      
      if (!Number(this.endDate)) return;

      // Scrolling banner coordinates loading
      if (!this.closest('.marquee-item')) {
        window.initLazyScript(this, this.init.bind(this));
      }
    }

    /**
     * Calculate next reset point in user's local timezone
     * Ensures timer resets at midnight at consistent intervals (e.g., every 2 days)
     */
    calculateClientTimezoneReset() {
      const now = new Date();
      const intervalDays = this.resetIntervalDays;
      
      // Get midnight tonight (start of tomorrow) in user's timezone
      const tomorrowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      
      // Calculate days since epoch for tomorrow
      const epochMidnight = new Date(1970, 0, 1);
      const daysSinceEpochTomorrow = Math.floor((tomorrowMidnight - epochMidnight) / (24 * 60 * 60 * 1000));
      
      // Find the next day that's on an interval boundary
      const daysUntilNextInterval = intervalDays - (daysSinceEpochTomorrow % intervalDays);
      
      // If daysUntilNextInterval equals intervalDays, the next interval is tonight at midnight
      // Otherwise, add the calculated days
      const daysToAdd = daysUntilNextInterval === intervalDays ? 0 : daysUntilNextInterval - 1;
      
      const nextReset = new Date(tomorrowMidnight.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
      
      return nextReset.getTime();
    }

    init() {
      // Get all timer element references
      this.daysEl = this.querySelector('.js-days .countdown__number-part');
      this.hoursPart1El = this.querySelector('.js-hours .countdown__number-part-1');
      this.hoursPart2El = this.querySelector('.js-hours .countdown__number-part-2');
      this.minsPart1El = this.querySelector('.js-mins .countdown__number-part-1');
      this.minsPart2El = this.querySelector('.js-mins .countdown__number-part-2');
      this.secsPart1El = this.querySelector('.js-secs .countdown__number-part-1');
      this.secsPart2El = this.querySelector('.js-secs .countdown__number-part-2');

      // Time unit constants
      this.second = 1000;
      this.minute = 60 * this.second;
      this.hour = 60 * this.minute;
      this.day = 24 * this.hour;

      // Check if animations are enabled
      const numberTransitionDuration = getComputedStyle(this).getPropertyValue('--countdown-number-transition-duration');
      if (numberTransitionDuration) {
        this.numberTransitionDurationMS = CountdownTimer.durationToMS(numberTransitionDuration);
        this.animated = this.numberTransitionDurationMS > 0;
      }

      // Add transition elements for animations
      if (this.animated) {
        this.querySelectorAll('.countdown__number:is(.js-hours, .js-mins, .js-secs) .countdown__number-part').forEach((el) => {
          el.innerHTML = '';
          const span1 = document.createElement('span');
          span1.className = 'countdown__number-part-current';
          span1.textContent = '0';
          const span2 = document.createElement('span');
          span2.className = 'countdown__number-part-previous';
          span2.textContent = '0';
          el.append(span1, span2);
        });
      }

      // Start the timer
      this.timer();
      this.interval = setInterval(this.timer.bind(this), this.second);
      this.setAttribute('loaded', '');
    }

    timer() {
      const timeDiff = this.endDate - new Date();

      if (timeDiff < 0) {
        // Timer has expired
        clearInterval(this.interval);
        
        // Auto-reset if using client timezone with interval
        if (this.useClientTimezone && this.resetIntervalDays > 0) {
          // Recalculate next reset point
          this.endDate = this.calculateClientTimezoneReset();
          // Restart the interval
          this.interval = setInterval(this.timer.bind(this), this.second);
          return;
        }
        
        // Handle completion (non-reset mode)
        if (this.hideOnComplete) {
          this.style.display = 'none';
        }
        const endText = this.querySelector('.countdown__end-text');
        if (endText) {
          endText.style.display = 'block';
        }
        this.setAttribute('loaded', '');
        return;
      }

      // Calculate time units
      const days = Math.floor(timeDiff / this.day);
      const hours = Math.floor(timeDiff / this.hour);
      const mins = Math.floor(timeDiff / this.minute);
      const secs = Math.floor(timeDiff / this.second);

      // Update days
      if (days === 0) {
        this.daysEl.closest('.countdown__item').classList.add('countdown__item--is-0');
        this.daysEl.classList.add('countdown__number-part--is-0');
      } else {
        this.daysEl.closest('.countdown__item').classList.remove('countdown__item--is-0');
        this.daysEl.classList.remove('countdown__number-part--is-0');
      }
      this.daysEl.textContent = days;

      // Update hours (00-23 format)
      const displayHours = String(hours - days * 24).padStart(2, '0');
      this.setPartValue(this.hoursPart1El, displayHours[0], true);
      this.setPartValue(this.hoursPart2El, displayHours[1]);

      // Update minutes (00-59 format)
      const displayMins = String(mins - hours * 60).padStart(2, '0');
      this.setPartValue(this.minsPart1El, displayMins[0], true);
      this.setPartValue(this.minsPart2El, displayMins[1]);

      // Update seconds (00-59 format)
      const displaySecs = String(secs - mins * 60).padStart(2, '0');
      this.setPartValue(this.secsPart1El, displaySecs[0], true);
      this.setPartValue(this.secsPart2El, displaySecs[1]);

      this.setAttribute('loaded', '');
    }

    /**
     * Update a timer part with new value and optional animation
     */
    setPartValue(part, value, setZeroClass) {
      if (setZeroClass) {
        part.classList.toggle('countdown__number-part--is-0', value === '0');
      }

      // Animated transitions
      if (this.animated) {
        const current = part.querySelector('.countdown__number-part-current');
        if (value !== current.textContent) {
          const previous = part.querySelector('.countdown__number-part-previous');
          part.classList.add('countdown__number-part--start-transition');
          previous.textContent = current.textContent;
          current.textContent = value;
          setTimeout(() => {
            requestAnimationFrame(() => part.classList.remove('countdown__number-part--start-transition'));
          }, 20);
        }
      } else {
        // No animation - direct update
        part.textContent = value;
      }
    }

    /**
     * Convert CSS duration to milliseconds
     */
    static durationToMS(duration) {
      if (duration.indexOf('ms') > 0) {
        return parseInt(duration, 10);
      }

      if (duration.indexOf('s') > 0) {
        return parseFloat(duration) * 1000;
      }

      throw new Error(`Duration unit not recognised: ${duration}`);
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}